import { useEffect, useEffectEvent, useRef } from "react";

interface TurnstileRenderOptions {
  sitekey: string;
  action: string;
  appearance: "interaction-only";
  callback: (token: string) => void;
  "expired-callback": () => void;
  "error-callback": (errorCode: string) => void;
}

interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

interface CloudflareTurnstileProps {
  siteKey: string;
  action: string;
  resetKey: number;
  onTokenChange: (token: string | null) => void;
}

const TURNSTILE_SCRIPT_ID = "cloudflare-turnstile-script";

export function CloudflareTurnstile(props: CloudflareTurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onTokenChange = useEffectEvent(props.onTokenChange);

  useEffect(() => {
    if (!props.siteKey) {
      return;
    }

    let widgetId: string | null = null;
    let cancelled = false;

    function renderWidget() {
      if (cancelled || !window.turnstile || !containerRef.current) {
        return;
      }

      widgetId = window.turnstile.render(containerRef.current, {
        sitekey: props.siteKey,
        action: props.action,
        appearance: "interaction-only",
        callback(token) {
          onTokenChange(token);
        },
        "expired-callback"() {
          onTokenChange(null);
        },
        "error-callback"(errorCode) {
          console.error("[Turnstile] Challenge failed", {
            action: props.action,
            errorCode,
            hostname: window.location.hostname,
          });
          onTokenChange(null);
        },
      });
    }

    const existingScript = document.getElementById(
      TURNSTILE_SCRIPT_ID,
    ) as HTMLScriptElement | null;
    const script = existingScript ?? document.createElement("script");

    if (window.turnstile) {
      renderWidget();
    } else {
      script.addEventListener("load", renderWidget);

      if (!existingScript) {
        script.id = TURNSTILE_SCRIPT_ID;
        script.src =
          "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
    }

    return () => {
      cancelled = true;
      script.removeEventListener("load", renderWidget);

      if (widgetId && window.turnstile) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [props.action, props.resetKey, props.siteKey]);

  if (!props.siteKey) {
    return null;
  }

  return <div ref={containerRef} data-action={props.action} />;
}
